import { courseTabsClasses } from "@/classes/courseTabsClasses";
import { cn } from "@/lib/utils";
import { ComponentProps, forwardRef } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/base/tooltip";
import { Img } from "@/components/base/img";

interface CourseTabTitleProps extends ComponentProps<"h3"> {
  tooltipImage?: string;
  tooltipImages?: string[];
}

const screenProps = {
  contain: true,
  className: "aspect-[197/398] w-50 mx-auto",
};

// Component to render text with colored keyword
const ImageDescriptionText = ({ text, keyword }: { text: string; keyword: string }) => {
  const parts = text.split(keyword);
  return (
    <p className="fs-16 font-semibold text-black mb-4 text-center">
      {parts[0]}
      <span style={{ color: "#F99C55" }}>{keyword}</span>
      {parts[1]}
    </p>
  );
};

const ConversationTooltipContent = () => {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-6 w-full">
      <div className="flex flex-col items-center w-full">
        <ImageDescriptionText
          text="This is how a conversation appears in the mobile app"
          keyword="conversation"
        />
        <Img src="/images/screens/convscreen1.png" className="aspect-[197/398] w-50 flex-shrink-0" />
      </div>
      <div className="flex flex-col items-center w-full">
        <ImageDescriptionText
          text="This is how a question appears in the mobile app"
          keyword="question"
        />
        <Img src="/images/screens/convscreen2.png" className="aspect-[197/398] w-50 flex-shrink-0" />
      </div>
    </div>
  );
};

export const CourseTabTitle = forwardRef<HTMLHeadingElement, CourseTabTitleProps>(
  ({ tooltipImage, tooltipImages, children, ...props }, ref) => {
    const hasTooltip = tooltipImage || tooltipImages;

    return (
      <h3
        {...props}
        ref={ref}
        className={cn(
          courseTabsClasses.navTitle,
          "fs-22 rounded px-4 py-2 text-center font-semibold whitespace-nowrap capitalize",
          "flex items-center justify-center gap-2",
          props.className,
        )}
      >
        {children}
        {hasTooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                aria-label="See how"
              >
                <Img src="/images/icons/Vector.png" alt="Info" className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              portal={document.getElementById("tooltip-backdrop")}
              variant="default"
              side="top"
              className={tooltipImages ? "!w-auto !max-w-none" : undefined}
            >
              {tooltipImages ? (
                <ConversationTooltipContent />
              ) : tooltipImage ? (
                <div className="flex flex-col items-center">
                  {tooltipImage.includes("chapterscreen.png") && (
                    <ImageDescriptionText
                      text="This is how chapters appear in the mobile app"
                      keyword="chapters"
                    />
                  )}
                  {tooltipImage.includes("lessonscreen.png") && (
                    <ImageDescriptionText
                      text="This is how lessons appear in the mobile app"
                      keyword="lessons"
                    />
                  )}
                  <Img src={tooltipImage} alt="Tooltip Image" className="aspect-[197/398] w-50" />
                </div>
              ) : null}
            </TooltipContent>
          </Tooltip>
        )}
      </h3>
    );
  },
);
